import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { Multiselect, DropdownList } from 'react-widgets';
import Custom from '../Multiselects/Custom';
import DatePicker from './DataComponent__DatePicker';
import DynamicSelectSegment from '../DynamicSelectSegment';
import ButtonsList from '../ButtonsList';
import InputGroupMultiselect from '../InputGroupMultiselect';
import CustomNotify from '../Notify';
import Button from '../Button';
import debounce from '../../helpers/debounce';
import query from '../../helpers/query';
import config from '../../config';
import { useI18n } from 'utils/I18n/context';
import { useLocation } from 'react-router-dom';

const initPage = config.initialPage;
const minLength = 3;

const templateKeys = {
    datepicker: 'getDatepicker',
    select: 'getSelect',
    multiselect: 'getMultiselect',
    dropdown: 'getDropdown',
    textInput: 'getTextInput',
    custom: 'getCustom',
    dynamic: 'getDynamic',
    goalTree: 'getSelectGoalTree',
    currency: 'getNull',
};

function DataComponentFilter(props) {
    const { t } = useI18n();
    const location = useLocation();
    const staticNames = t?.dataTable ?? {};
    const staticNamesActions = t?.actions ?? {};
    const messagesForSelect = useMemo(() => ({ emptyList: staticNames.emptyList }), [staticNames.emptyList]);

    const initial = useMemo(() => {
        const parsedQuery = query.parse(location.search || '');
        const defValues = {};
        (props.fields || []).map((item) => {
            if (!item?.value) return;
            if (item.name === 'datepicker') {
                if (item.value.startDate) defValues[`${item.key}StartDate`] = item.value.startDate;
                if (item.value.endDate) defValues[`${item.key}EndDate`] = item.value.endDate;
            } else {
                defValues[item.key] = item.value;
            }
        });

        const valuesBase = {
            page: parsedQuery.page || initPage,
            ...defValues,
            ...parsedQuery,
        };

        if (location.pathname !== '/office/finances/report') {
            valuesBase.createStartDate =
                parsedQuery.startDate || props.startDate || moment().subtract(config.defaultDatepickerDaysPeriod, 'days');
            valuesBase.createEndDate = parsedQuery.endDate || props.endDate || moment().endOf('day');
        }

        const selected = [];
        (props.fields || []).map((item, i) => {
            if ((item.name === 'datepicker' && valuesBase[`${item.key}StartDate`]) || valuesBase[item.key]) {
                selected.push({ ...item, fieldIndex: i });
            }
        });

        return { values: valuesBase, selected };
    }, [props.fields, props.startDate, props.endDate, location.pathname, location.search]);

    const [values, setValues] = useState(initial.values);
    const [selected, setSelected] = useState(initial.selected);

    const debouncedNotifyRef = useRef(
        debounce((name, val, tt) => {
            const len = typeof val === 'string' ? val.length : 0;
            if (len > 0 && len <= minLength) {
                const message = tt?.validate?.minLineLength
                    ? tt.validate.minLineLength.replace(/%s/i, minLength)
                    : `Min length is ${minLength}`;
                CustomNotify(message, 'error');
            }
        }, 500)
    );

    useEffect(() => {
        onAction('firstRender');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getValue = useCallback(
        (data) => {
            if (data.name === 'datepicker') {
                return {
                    startDate: values[`${data.key}StartDate`] || moment().subtract(config.defaultDatepickerDaysPeriod, 'days'),
                    endDate: values[`${data.key}EndDate`] || moment().endOf('day'),
                };
            }
            if (data.name === 'textInput') return values[data.key] || '';
            return values[data.key];
        },
        [values]
    );

    const getNull = useCallback(() => null, []);

    const onDataPickerApply = useCallback((datepicker, key) => {
        setValues((v) => ({
            ...v,
            [`${key}StartDate`]: datepicker.startDate.clone().startOf('day'),
            [`${key}EndDate`]: datepicker.endDate.clone().endOf('day'),
        }));
    }, []);

    const onTextInputChange = useCallback(
        (e) => {
            const { name, value } = e.target;
            setValues((v) => {
                debouncedNotifyRef.current(name, value, t);
                return { ...v, [name]: value };
            });
        },
        [t]
    );

    const onSelectChange = useCallback((e) => {
        const { name, value } = e.target;
        setValues((v) => ({ ...v, [name]: value }));
    }, []);

    const onChange = useCallback((value, key) => {
        setValues((v) => ({ ...v, [key]: value }));
        onAction();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangeDynamic = useCallback((value, key) => {
        const newValue = value.map((item) => ({ title: item.title, value: item.value }));
        setValues((v) => ({ ...v, [key]: newValue }));
    }, []);

    const deleteValue = useCallback((vals, item) => {
        const res = { ...vals };
        if (item.name === 'datepicker') {
            delete res[`${item.key}StartDate`];
            delete res[`${item.key}EndDate`];
        } else {
            delete res[item.key];
        }
        return res;
    }, []);

    function onAction(type) {
        setValues((v) => {
            const next = { ...v, page: initPage };
            props.onAction && props.onAction(next, type);
            return next;
        });
    }

    const onChangeFilter = useCallback(
        (item, index) => {
            const fieldIndex = (props.fields || []).findIndex((d) => d.key === item.key);
            setSelected((sel) => {
                const copy = [...sel];
                const oldItem = copy[index];
                setValues((v) => deleteValue(v, oldItem));
                copy[index] = { ...item, fieldIndex };
                return copy;
            });
        },
        [props.fields, deleteValue]
    );

    const onAddFilter = useCallback(
        (item) => {
            const fieldIndex = (props.fields || []).findIndex((d) => d.key === item.key);
            setSelected((sel) => [...sel, { ...item, fieldIndex }]);
        },
        [props.fields]
    );

    const onDelete = useCallback(
        (item, index) => {
            setSelected((sel) => {
                const copy = [...sel];
                const deletedItem = copy.find(({ name }) => name === item.name);
                const idx = index === undefined ? copy.indexOf(deletedItem) : index;
                if (idx < 0) return copy;
                setValues((v) => deleteValue(v, deletedItem));
                copy.splice(idx, 1);
                return copy;
            });
        },
        [deleteValue]
    );

    const getDatepicker = useCallback(
        (data) => {
            const onApply = (range) => onDataPickerApply(range, data.key);
            return (
                <DatePicker
                    onApply={onApply}
                    className="btn form-control reactdaypicker-block"
                    {...getValue(data)}
                />
            );
        },
        [getValue, onDataPickerApply]
    );

    const getSelect = useCallback(
        (select) => (
            <select
                onChange={onSelectChange}
                className="form-control long-input right"
                name={select.key}
                value={getValue(select)}
            >
                {(props.fields[select.fieldIndex].data || []).map((option, i) => (
                    <option key={i} value={option.value}>
                        {option.title}
                    </option>
                ))}
            </select>
        ),
        [getValue, onSelectChange, props.fields]
    );

    const getDropdown = useCallback(
        (dropdown) => (
            <DropdownList
                messages={messagesForSelect}
                className="rw-dropdownlist-select"
                dataKey="value"
                textField="title"
                placeholder={dropdown.placeholder || ''}
                data={props.fields[dropdown.fieldIndex].data}
                value={getValue(dropdown)}
                onChange={(value) => onChange(value, dropdown.key)}
            />
        ),
        [messagesForSelect, props.fields, getValue, onChange]
    );

    const getMultiselect = useCallback(
        (select) => (
            <Multiselect
                messages={messagesForSelect}
                placeholder={select.placeholder || ''}
                className="right"
                dataKey="value"
                textField="title"
                onChange={(value) => onChange(value, select.key)}
                data={Array.isArray(props.fields[select.fieldIndex]?.data) ? props.fields[select.fieldIndex].data : []}
                name={select.key}
                value={Array.isArray(getValue(select)) ? getValue(select) : []}
            />
        ),
        [messagesForSelect, props.fields, getValue, onChange]
    );

    const getTextInput = useCallback(
        (data) => (
            <input
                className="form-control"
                type="text"
                name={data.key}
                onChange={onTextInputChange}
                placeholder={data.placeholder}
                value={getValue(data)}
            />
        ),
        [getValue, onTextInputChange]
    );

    const getCustom = useCallback(
        (data) => {
            const onChangeCustom = (item) => onChange(item, data.key);
            return <Custom segment={data.label} values={getValue(data)} onAction={onChangeCustom} />;
        },
        [getValue, onChange]
    );

    const getDynamic = useCallback(
        (data) => {
            const onChangeDyn = (item) => onChangeDynamic(item, data.key);
            return (
                <DynamicSelectSegment
                    segment={data.segment}
                    multiselect
                    values={getValue(data)}
                    onAction={onChangeDyn}
                />
            );
        },
        [getValue, onChangeDynamic]
    );

    const getSelectGoalTree = useCallback(
        (data) => {
            const onChangeSeg = (item) => onChange(item, data.key);
            return <InputGroupMultiselect nesting={data.nesting} onChange={onChangeSeg} tags={getValue(data)} />;
        },
        [getValue, onChange]
    );

    const getChangeFunction = useCallback(
        (value, key, name) => {
            if (name === 'currency' || key === 'currency') return onChange(value, key);
            if (name === 'datepicker') return onDataPickerApply(value, key);
            alert('No function assigned');
        },
        [onChange, onDataPickerApply]
    );

    const renderFilter = useCallback(
        (item = {}, i) => {
            const onChangeFilterLocal = (it) => onChangeFilter(it, i);
            const onChangeDropdown = i >= 0 ? onChangeFilterLocal : onAddFilter;
            const onDeleteLocal = () => onDelete(item, i);

            const selectedField = (i >= 0 && item && item.key)
                ? (props.fields || []).find((d) => d.key === item.key)
                : null;

            return (
                <div className={`table-filter ${props.gray ? ' table-filter-gray' : ''}`} key={`${item.key || 'new-filter'}-${i >= 0 ? i : 'add'}`}>
                    {item.withoutDropdown ? null : (
                        <div className="table-filter__item">
                            <label>{item.dropdownLabel || staticNames.addFilter}</label>
                            <DropdownList
                                className="rw-dropdownlist-select"
                                placeholder={staticNames.addFilter}
                                dataKey="key"
                                textField="label"
                                data={props.fields}
                                disabled={!!item.disabled}
                                value={selectedField || null}
                                onChange={onChangeDropdown}
                                popupProps={{ style: { maxHeight: 260, overflowY: 'auto' } }}
                            />
                        </div>
                    )}

                    {item.name ? (
                        <>
                            <div className="table-filter__item payouts-block">
                                {item.label && (
                                    <label>
                                        {item.label}
                                        {item.name !== 'datepicker' ? <i className="fa fa-search" /> : null}
                                    </label>
                                )}
                                {({
                                    getDatepicker,
                                    getSelect,
                                    getMultiselect,
                                    getDropdown,
                                    getTextInput,
                                    getCustom,
                                    getDynamic,
                                    getSelectGoalTree,
                                    getNull,
                                }[templateKeys[item.name]] || getNull)(item)}
                            </div>

                            {!item.disabled || item.name === 'datepicker' ? (
                                <div className="table-filter__item control-buttons">
                                    <Button
                                        loading={props.loading}
                                        extraClass={{ 'apply-btn': true }}
                                        text={staticNamesActions.apply}
                                        onClick={() => onAction()}
                                    />
                                    <Button
                                        extraClass={{ 'reset-btn': true, hidden: props.hideResetButton }}
                                        text={staticNamesActions.reset}
                                        onClick={onDeleteLocal}
                                    />
                                </div>
                            ) : null}
                        </>
                    ) : null}

                    {item.withList ? (
                        <ButtonsList
                            fields={props.fields}
                            data={item}
                            onChange={(value, key) => getChangeFunction(value, key, item.name)}
                            currentValue={values[item.key]}
                            showSubtitle={item.showSubtitle}
                        />
                    ) : null}
                </div>
            );
        },
        [
            props.gray,
            props.fields,
            props.loading,
            props.hideResetButton,
            staticNames.addFilter,
            staticNamesActions.apply,
            staticNamesActions.reset,
            selected,
            values,
            onChangeFilter,
            onAddFilter,
            onDelete,
            getChangeFunction,
            onAction,
        ]
    );

    if (!props.fields.length) return null;

    return (
        <div className="ibox-title">
            {selected.map((filter, i) => renderFilter(filter, i))}
            {props.fields.length !== selected.length ? renderFilter() : null}
        </div>
    );
}

DataComponentFilter.propTypes = {
    fields: PropTypes.array,
    gray: PropTypes.bool,
    loading: PropTypes.bool,
    hideResetButton: PropTypes.bool,
    onAction: PropTypes.func,
    startDate: PropTypes.any,
    endDate: PropTypes.any,
};

DataComponentFilter.defaultProps = {
    fields: [],
    gray: false,
    loading: false,
    hideResetButton: false,
};

export default DataComponentFilter;
